import { Divider } from "antd";
import { ErrorMessage, type FormikErrors } from "formik";
import { Component } from "react";

import { BTSplitButton } from "@btnet/core-components";
import { BTPopover } from "@btnet/core-components/BTPopover";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { type BTSelectItem } from "types/apiResponse/apiResponse";
import { CostType } from "types/enum";

import { getSelectedValue, getValidateStatusTouched } from "utilities/form/form";
import yup from "utilities/form/yup";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTCheckbox } from "commonComponents/btWrappers/BTCheckbox/BTCheckbox";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { btDeleteConfirm } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTForm, BTFormItem } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTInput } from "commonComponents/btWrappers/BTInput/BTInput";
import { BTLayoutContent } from "commonComponents/btWrappers/BTLayout/BTLayout";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTModalLayout } from "commonComponents/btWrappers/BTModal/BTModalLayout";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { BTTextArea } from "commonComponents/btWrappers/BTTextArea/BTTextArea";
import { Currency } from "commonComponents/financial/Currency/Currency";
import { DateDisplay } from "commonComponents/utilities/DateDisplay/DateDisplay";
import { getUnsavedChangesModalConfig } from "commonComponents/utilities/DirtyTracking/UnsavedChangesPrompt";
import {
    type IBTInjectedFormikProps,
    withFormikBT,
} from "commonComponents/utilities/Formik/WithFormikBT";
import { Hotkey } from "commonComponents/utilities/Hotkey/Hotkey";
import { HotkeyCommands } from "commonComponents/utilities/Hotkey/hotkey.utility";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";
import { FormikValidationSummary } from "commonComponents/utilities/validationSummary/FormikValidationSummary/FormikValidationSummary";

import {
    type CostCodeDetailsFormActions,
    type CostCodeEntity,
    type ICostCodeDetailsFormValues,
    type ParentExtraData,
} from "./CostCode.api.types";

interface ICostCodeDetailsProps {
    entity: CostCodeEntity;
    actionBeingPerformed: CostCodeDetailsFormActions;
    laborCodesOnly?: boolean;
    onSubmit: (
        formData: ICostCodeDetailsFormValues,
        action: CostCodeDetailsFormActions,
        setErrors: (errors: FormikErrors<ICostCodeDetailsFormValues>) => void,
    ) => Promise<boolean>;
    onDelete: () => Promise<void>;
    disableDelete?: boolean;
    title?: string;
    modalConfig?: IModalConfiguration;
}

class CostCodeDetailsInternal extends Component<
    IBTInjectedFormikProps<ICostCodeDetailsProps, ICostCodeDetailsFormValues>
> {
    private handleUpdateCostCodeCategory = (field: string, value: any) => {
        this.props.setFieldValue(field, value);
        this.props.setFieldValue("parentId", 0);
    };

    private checkCategoryIdExists = (id: number): boolean => {
        const matchingCategory = this.props.entity.categoryList.find(
            (category: BTSelectItem) => parseInt(category.id) === id,
        );
        return !!matchingCategory;
    };

    private handleUpdateSubCodeOf = (
        field: string,
        value: any,
        selectedItem: BTSelectItem<ParentExtraData> | undefined,
    ) => {
        this.props.setFieldValue(field, value);
        if (selectedItem && selectedItem.extraData && selectedItem.extraData.categoryId > 0) {
            // Set selectedCategoryId to same CategoryId as SubCodeOf field parent category
            // Only if sub code is valid
            const fieldValue = this.checkCategoryIdExists(selectedItem.extraData.categoryId)
                ? selectedItem.extraData.categoryId
                : null;
            this.props.setFieldValue("selectedCategoryId", fieldValue);
        }
    };

    private renderCostCodeSection = () => {
        const { entity, values, touched, errors, handleChange, setFieldValue, setFieldTouched } =
            this.props;

        const isNew = entity.costCodeId === 0;

        return (
            <div className="CostCodeDetailsContainer">
                <BTRow gutter={8}>
                    <BTCol xs={24} sm={12}>
                        <BTFormItem
                            label="Title"
                            required
                            validateStatus={getValidateStatusTouched(errors, touched, "title")}
                            help={<ErrorMessage name="title" />}
                        >
                            <BTInput
                                id="title"
                                data-testid="title"
                                value={values.title}
                                onChange={setFieldValue}
                                onBlur={setFieldTouched}
                                disabled={entity.shouldLockCostCode}
                                autoFocus={isNew}
                            />
                        </BTFormItem>
                    </BTCol>
                    <BTCol xs={24} sm={12}>
                        <BTFormItem
                            label="Category"
                            required
                            validateStatus={getValidateStatusTouched(
                                errors,
                                touched,
                                "selectedCategoryId",
                            )}
                            help={<ErrorMessage name="selectedCategoryId" />}
                        >
                            <BTSelect<ICostCodeDetailsFormValues>
                                id="selectedCategoryId"
                                data-testid="selectedCategoryId"
                                value={values.selectedCategoryId}
                                onChange={this.handleUpdateCostCodeCategory}
                                onBlur={setFieldTouched}
                                treeData={entity.categoryList}
                                disabled={entity.shouldLockCostCode}
                            />
                        </BTFormItem>
                    </BTCol>
                </BTRow>

                <BTFormItem
                    label="Sub code of"
                    validateStatus={getValidateStatusTouched(errors, touched, "parentId")}
                    help={<ErrorMessage name="parentId" />}
                >
                    <BTSelect<ICostCodeDetailsFormValues, ParentExtraData>
                        id="parentId"
                        data-testid="parentCostCodeId"
                        value={values.parentId}
                        onChange={this.handleUpdateSubCodeOf}
                        onBlur={setFieldTouched}
                        treeData={entity.parentList}
                        disabled={entity.shouldLockCostCode}
                    />
                </BTFormItem>

                {entity.currentType === CostType.Cost && (
                    <BTRow align="middle" data-testid="costCodeRow">
                        <BTCol xs={24} sm={12}>
                            <BTFormItem
                                validateStatus={getValidateStatusTouched(
                                    errors,
                                    touched,
                                    "isLabor",
                                )}
                                help={<ErrorMessage name="isLabor" />}
                            >
                                <BTCheckbox
                                    id="isLabor"
                                    data-testid="isLabor"
                                    checked={values.isLabor}
                                    onChange={handleChange}
                                    disabled={
                                        entity.shouldLockCostCode || this.props.laborCodesOnly
                                    }
                                >
                                    Time clock labor code
                                </BTCheckbox>
                                <BTPopover
                                    content="Checking this box will make this code available on the time clock"
                                    removeSpacing
                                />
                            </BTFormItem>
                        </BTCol>

                        <BTCol xs={24} sm={12}>
                            {values.isLabor && !entity.shouldLockCostCode && (
                                <BTFormItem
                                    required
                                    label={
                                        <>
                                            Default Labor Cost
                                            <BTPopover content="This allows you to associate a fixed cost rate to a specific cost code. Hourly rate tracking needs to be set to “By cost code” within time clock setup." />
                                        </>
                                    }
                                    validateStatus={getValidateStatusTouched(
                                        errors,
                                        touched,
                                        "defaultUnitCost",
                                    )}
                                    help={<ErrorMessage name="defaultUnitCost" />}
                                >
                                    <Currency<ICostCodeDetailsFormValues>
                                        id="defaultUnitCost"
                                        data-testid="defaultUnitCost"
                                        disabled={entity.byUserHourlyTracking}
                                        value={values.defaultUnitCost}
                                        onChange={setFieldValue}
                                        onBlur={setFieldTouched}
                                        max={1000000000}
                                        min={-1000000000}
                                    />
                                </BTFormItem>
                            )}
                        </BTCol>
                    </BTRow>
                )}

                <Divider />

                <BTFormItem
                    label="Details"
                    validateStatus={getValidateStatusTouched(errors, touched, "details")}
                    help={<ErrorMessage name="details" />}
                >
                    <BTTextArea
                        id="details"
                        data-testid="details"
                        value={values.details}
                        maxLength={512}
                        onChange={(field, value) => {
                            setFieldValue(field, value);
                        }}
                        onBlur={setFieldTouched}
                        disabled={entity.shouldLockCostCode}
                    />
                </BTFormItem>

                <Divider />

                {(this.props.values.currentType === CostType.Variance ||
                    this.props.values.currentType === CostType.Both) && (
                    <BTFormItem
                        validateStatus={getValidateStatusTouched(
                            errors,
                            touched,
                            "isCustomerVariance",
                        )}
                        help={<ErrorMessage name="isCustomerVariance" />}
                    >
                        <BTCheckbox
                            id="isCustomerVariance"
                            data-testid="isCustomerVariance"
                            checked={values.isCustomerVariance}
                            onChange={handleChange}
                        >
                            Customer variance
                        </BTCheckbox>
                    </BTFormItem>
                )}

                <BTCheckbox
                    id="isActive"
                    data-testid="isActive"
                    checked={values.isActive}
                    onChange={handleChange}
                    disabled={entity.shouldLockCostCode}
                >
                    Active
                </BTCheckbox>
            </div>
        );
    };

    static contextType = BuilderInfoContext;
    declare context: React.ContextType<typeof CostCodeDetailsInternal.contextType>;

    private handleDelete = () => {
        btDeleteConfirm({
            entityName: `code`,
            content: `Are you sure you want to delete this code?`,
            onOk: this.props.onDelete,
        });
    };

    private handleSave = async () => {
        await this.handleCustomSubmit("save");
    };

    private handleSaveAndNew = async () => {
        await this.handleCustomSubmit("saveAndNew");
    };

    private handleSaveAndClose = async () => {
        await this.handleCustomSubmit("saveAndClose");
    };

    private getModalConfig = () => {
        const { getDirtyTrackingProps, modalConfig } = this.props;
        return getUnsavedChangesModalConfig(
            getDirtyTrackingProps,
            {
                onCloseWithSave: this.handleSaveAndClose,
                onCloseWithoutSave: modalConfig?.beforeClose,
            },
            modalConfig?.parentRoute,
        );
    };

    private handleCustomSubmit = async (action: CostCodeDetailsFormActions) => {
        this.props.handleSubmit();
        this.props.setSubmitting(true);
        const formErrors = await this.props.validateForm();
        if (Object.keys(formErrors).length === 0) {
            const success = await this.props.onSubmit(
                this.props.values,
                action,
                this.props.setErrors,
            );
            if (action === "saveAndClose" && success) {
                this.props.modalConfig?.beforeClose();
            } else if (action === "saveAndNew" && success) {
                this.props.resetForm();
            }
        }
        this.props.setSubmitting(false);
    };

    render() {
        const { entity, actionBeingPerformed, values, errors, disableDelete, modalConfig } =
            this.props;

        const actions = (
            <>
                {entity.canSave && (
                    <>
                        <Hotkey
                            onCommand={this.handleSave}
                            triggerBehavior="down"
                            {...HotkeyCommands.save}
                        />
                        <BTSplitButton<CostCodeDetailsFormActions>
                            data-testid="saveAndClose"
                            actionBeingPerformed={actionBeingPerformed}
                            loadingAction="saveAndClose"
                            placement="topRight"
                            buttonAction={{
                                content: "Save and close",
                                onClick: this.handleSaveAndClose,
                                "data-testid": "saveAndClose",
                                hotkey: "saveAndClose",
                            }}
                            menuItems={[
                                {
                                    content: "Save",
                                    onClick: this.handleSave,
                                    "data-testid": "save",
                                    hotkey: "save",
                                },
                                {
                                    content: "Save and new",
                                    onClick: this.handleSaveAndNew,
                                    "data-testid": "saveAndNew",
                                },
                            ]}
                            type="primary"
                        />
                    </>
                )}
                {entity.costCodeId > 0 && entity.canDelete && (
                    <BTButton<CostCodeDetailsFormActions>
                        type="secondary"
                        loadingAction="delete"
                        actionBeingPerformed={actionBeingPerformed}
                        data-testid="deleteButton"
                        onClick={this.handleDelete}
                        disabled={disableDelete}
                        hotkey="delete"
                    >
                        Delete
                    </BTButton>
                )}
                {!entity.isActive && entity.costCodeId > 0 && (
                    <div style={{ display: "inline-block" }} className="margin-left-md">
                        (Inactive since <DateDisplay value={entity.inactivationDate} />)
                    </div>
                )}
            </>
        );

        const content = (
            <BTForm className="CostCodeDetailsContainer">
                <BTLayoutContent>
                    <FormikValidationSummary
                        errors={errors}
                        values={values}
                        showAfterSubmit={this.props.submitCount}
                        scheme={CostCodeDetailsValidators}
                    />
                    <PageSection>{this.renderCostCodeSection()}</PageSection>
                </BTLayoutContent>
            </BTForm>
        );

        if (!modalConfig) return content;

        return (
            <BTModalLayout
                title={this.props.title}
                modalConfig={this.getModalConfig()}
                footerContent={actions}
            >
                {content}
            </BTModalLayout>
        );
    }
}

const CostCodeDetailsValidators = yup.object().shape<ICostCodeDetailsFormValues>({
    costCodeId: yup.number().label("Cost Code Id"),
    isActive: yup.boolean().label("Active"),
    isLabor: yup.boolean().label("Time Clock Labor Code"),
    isCustomerVariance: yup.boolean().label("Customer Variance"),
    title: yup.string().required().max(80, "Title cannot exceed 80 characters").label("Title"),
    selectedCategoryId: yup.number().requiredDropdown().label("Cost Code Category"),
    parentId: yup.number().required().label("Sub Code Of"),
    currentType: yup.mixed().oneOf([0, 1, -999]),
    details: yup.string().max(512).label("Details"),
    defaultUnitCost: yup
        .number()
        .when("isLabor", {
            is: true,
            then: yup.number().required().min(-1000000000).max(1000000000),
        })
        .label("Default Labor Cost"),
});

export const CostCodeDetailsPresentational = withFormikBT<
    ICostCodeDetailsProps,
    ICostCodeDetailsFormValues
>({
    mapPropsToValues: (props: ICostCodeDetailsProps) => {
        const { entity } = props;

        return {
            costCodeId: entity.costCodeId,
            isActive: entity.costCodeId === 0 || entity.isActive,
            isLabor: entity.isLaborCode || props.laborCodesOnly || false,
            isCustomerVariance: entity.isCustomerVariance,
            title: entity.title || "",
            selectedCategoryId: getSelectedValue(entity.categoryList),
            parentId: entity.parentCostCodeId,
            currentType: entity.currentType,
            details: entity.details || "",
            defaultUnitCost: entity.defaultUnitCost,
        };
    },

    validationSchema: () => CostCodeDetailsValidators,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    handleSubmit: async () => {},
})(CostCodeDetailsInternal);
